<script setup lang="ts">
import type { TimelineItemState, VTimelineProps } from "./VTimeline.types";
import VIcon from "../Icon/VIcon.vue";

defineOptions({
  name: "VTimeline",
});

/**
 * A vertical list of steps with a 28px marker each, joined by a line: the progress of one thing over time, such as the
 * status of a guest report. A done step is a filled primary circle with a check, the current step a light primary
 * circle with a dot and a pulsing ring, a step still to come a grey ring.
 *
 * When the list appears the steps rise in one after the other, the line of a done step grows down from its marker,
 * then the marker of the current step pops in and starts to pulse. With reduced motion the list shows its final
 * state at once and nothing moves.
 */
const { items } = defineProps<VTimelineProps>();

const MARKER_CLASS: Record<TimelineItemState, string> = {
  done: "border-primary-600 bg-primary-600 text-white",
  current: "timeline-marker--current border-primary-600 bg-primary-50 [--timeline-pulse:rgb(76_175_80/0.45)] dark:border-primary-400 dark:bg-primary-900/40 dark:[--timeline-pulse:rgb(102_187_106/0.55)]",
  todo: "border-gray-300 bg-white dark:border-dark-600 dark:bg-dark-800",
  skipped: "border-dashed border-gray-300 bg-white dark:border-dark-600 dark:bg-dark-800",
};

const TITLE_CLASS: Record<TimelineItemState, string> = {
  done: "font-medium text-(--text)",
  current: "font-bold text-primary-900 dark:text-primary-200",
  todo: "font-normal text-gray-800 dark:text-gray-200",
  skipped: "font-normal text-gray-500 dark:text-gray-400",
};

function isLast(index: number): boolean {
  return index === items.length - 1;
}

/** The steps rise in 0.12s apart, top to bottom. */
const STEP_STAGGER_S = 0.12;

function enterDelay(index: number): string {
  return `${Math.round(index * STEP_STAGGER_S * 100) / 100}s`;
}
</script>

<template>
  <ol class="m-0 list-none p-0">
    <li
      v-for="(item, index) in items"
      :key="item.key ?? item.title"
      class="timeline-item relative grid grid-cols-[28px_minmax(0,1fr)] gap-x-3.5"
      :class="isLast(index) ? 'pb-1' : 'pb-[22px]'"
      :style="{ animationDelay: enterDelay(index) }"
      :aria-current="item.state === 'current' ? 'step' : undefined"
      :data-state="item.state"
      data-testid="timeline-item"
    >
      <span
        v-if="!isLast(index)"
        class="absolute top-8 bottom-1 left-[13px] w-0.5 rounded-[1px]"
        :class="item.state === 'done' ? 'timeline-connector--done bg-primary-600' : 'bg-gray-300 dark:bg-dark-600'"
        aria-hidden="true"
        data-testid="timeline-connector"
      />
      <span
        class="relative flex size-7 items-center justify-center rounded-full border-2"
        :class="MARKER_CLASS[item.state]"
        aria-hidden="true"
        data-testid="timeline-marker"
      >
        <VIcon v-if="item.state === 'done'" name="check" class="text-sm" :stroke-width="3" />
        <span v-else-if="item.state === 'current'" class="timeline-dot size-2.5 rounded-full bg-primary-600 dark:bg-primary-400" />
      </span>
      <div class="flex min-w-0 flex-col gap-0.5 pt-[3px]">
        <div class="flex items-baseline justify-between gap-2">
          <span class="text-sm leading-5" :class="TITLE_CLASS[item.state]">
            {{ item.title }}
            <span v-if="item.stateLabel" class="sr-only">, {{ item.stateLabel }}</span>
          </span>
          <time
            v-if="item.time && item.datetime"
            :datetime="item.datetime"
            class="shrink-0 text-[13px] leading-[18px] whitespace-nowrap text-gray-600 tabular-nums dark:text-gray-400"
          >
            {{ item.time }}
          </time>
          <span
            v-else-if="item.time"
            class="shrink-0 text-[13px] leading-[18px] whitespace-nowrap text-gray-600 tabular-nums dark:text-gray-400"
          >
            {{ item.time }}
          </span>
        </div>
        <p v-if="item.description" class="m-0 text-[13px] leading-[19px] text-gray-600 dark:text-gray-400">
          {{ item.description }}
        </p>
      </div>
    </li>
  </ol>
</template>

<style scoped>
/* The steps rise into place, one after the other (the delay is on the item). */
@keyframes timeline-in {
  from {
    opacity: 0;
    transform: translateY(8px);
  }

  to {
    opacity: 1;
    transform: none;
  }
}

/* The line of a done step grows down from its marker. */
@keyframes timeline-line {
  from {
    transform: scaleY(0);
  }

  to {
    transform: scaleY(1);
  }
}

/* The marker of the current step pops in once the steps above it are in place. */
@keyframes timeline-pop {
  0% {
    transform: scale(0.4);
  }

  100% {
    transform: scale(1);
  }
}

/* Then its ring keeps pulsing outwards, so the step reads as in progress. */
@keyframes timeline-pulse {
  0% {
    box-shadow: 0 0 0 0 var(--timeline-pulse);
  }

  70% {
    box-shadow: 0 0 0 12px rgb(76 175 80 / 0);
  }

  100% {
    box-shadow: 0 0 0 0 rgb(76 175 80 / 0);
  }
}

/* And its dot breathes with the pulse. */
@keyframes timeline-dot {
  0%,
  100% {
    transform: scale(1);
  }

  50% {
    transform: scale(0.7);
  }
}

.timeline-item {
  animation: timeline-in 0.4s ease-out both;
}

.timeline-connector--done {
  transform-origin: top;
  animation: timeline-line 0.35s ease-out 0.3s both;
}

.timeline-marker--current {
  box-shadow: 0 0 0 0 var(--timeline-pulse);
  animation:
    timeline-pop 0.45s cubic-bezier(0.2, 0.8, 0.3, 1.3) 0.55s both,
    timeline-pulse 1.8s ease-out 1s infinite;
}

.timeline-dot {
  animation: timeline-dot 1.8s ease-in-out 1s infinite;
}

@media (prefers-reduced-motion: reduce) {
  .timeline-item,
  .timeline-connector--done,
  .timeline-marker--current,
  .timeline-dot {
    animation: none;
  }
}
</style>
